import { createStore, del, get, set } from 'idb-keyval';
import { useEffect, useState } from 'react';
import { newId } from '../../doc/BoardDoc';
import { assetsUrl } from '../../app/base';
import { api } from '../../sync/api';
import { generateVariants, VARIANT_ORDER, type VariantName } from './variants';

/**
 * Asset cache and store. Originals and variants live in IndexedDB; anything missing locally is
 * fetched from the server (assets.php) and cached. New imports are uploaded by the upload queue.
 */

export interface LocalAssetMeta {
  id: string;
  mime: string;
  name: string;
  bytes: number;
  width: number;
  height: number;
  /** Variants that exist (locally or on the server), with pixel sizes. */
  variants: Partial<Record<VariantName, { w: number; h: number }>>;
  /** True once the server has it (or when the meta came from the server). */
  uploaded: boolean;
  /** 'file' for non-image attachments (no variants). */
  kind?: 'image' | 'file';
}

interface RemoteAssetMeta {
  id: string;
  boardId: string;
  mime: string;
  bytes: number;
  width: number;
  height: number;
  name: string;
  variants: Partial<Record<VariantName, { w: number; h: number; ext: string; bytes: number }>>;
}

export const assetsIdb = createStore('vds-assets', 'assets');
const metaKey = (id: string) => `meta:${id}`;
const blobKey = (id: string, v: VariantName) => `blob:${id}:${v}`;
const metaCache = new Map<string, LocalAssetMeta>();

export async function storeImageAsset(file: File): Promise<LocalAssetMeta> {
  const generated = await generateVariants(file);
  const id = newId();
  const meta: LocalAssetMeta = {
    id,
    mime: file.type,
    name: file.name,
    bytes: file.size,
    width: generated.width,
    height: generated.height,
    variants: { original: { w: generated.width, h: generated.height } },
    uploaded: false,
  };
  const writes: Promise<void>[] = [set(blobKey(id, 'original'), file, assetsIdb)];
  for (const [name, v] of Object.entries(generated.variants)) {
    const variant = name as Exclude<VariantName, 'original'>;
    meta.variants[variant] = { w: v.w, h: v.h };
    writes.push(set(blobKey(id, variant), v.blob, assetsIdb));
  }
  await Promise.all(writes);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  return meta;
}

/** Stores a non-image attachment as-is (no variants). */
export async function storeFileAsset(file: File): Promise<LocalAssetMeta> {
  const id = newId();
  const meta: LocalAssetMeta = {
    id,
    mime: file.type || 'application/octet-stream',
    name: file.name,
    bytes: file.size,
    width: 0,
    height: 0,
    variants: { original: { w: 0, h: 0 } },
    uploaded: false,
    kind: 'file',
  };
  await set(blobKey(id, 'original'), file, assetsIdb);
  await set(metaKey(id), meta, assetsIdb);
  metaCache.set(id, meta);
  return meta;
}

/** Copies an asset under a new id (for pasting into another board), fetching variants from the server if needed. */
export async function cloneAssetLocally(id: string): Promise<string | null> {
  const meta = await getAssetMeta(id);
  if (!meta) return null;
  const newId = newId_();
  const copy: LocalAssetMeta = { ...meta, id: newId, uploaded: false, variants: { ...meta.variants } };
  for (const v of VARIANT_ORDER) {
    if (!meta.variants[v]) continue;
    const blob = await getAssetBlob(id, v);
    if (blob) await set(blobKey(newId, v), blob, assetsIdb);
    else delete copy.variants[v];
  }
  if (!copy.variants.original) return null;
  await set(metaKey(newId), copy, assetsIdb);
  metaCache.set(newId, copy);
  return newId;
}
const newId_ = newId;

/** Opens/downloads an attachment: from the local cache, or the server when only there. */
export async function openAsset(id: string, name: string): Promise<void> {
  const blob = await getAssetBlob(id, 'original');
  if (!blob) {
    window.open(assetsUrl(id, 'original'), '_blank', 'noopener');
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export async function markUploaded(id: string): Promise<void> {
  const meta = await getAssetMeta(id);
  if (!meta || meta.uploaded) return;
  meta.uploaded = true;
  metaCache.set(id, meta);
  await set(metaKey(id), meta, assetsIdb);
}

export async function getAssetMeta(id: string, allowRemote = true): Promise<LocalAssetMeta | undefined> {
  const cached = metaCache.get(id);
  if (cached) return cached;
  const local = await get<LocalAssetMeta>(metaKey(id), assetsIdb);
  if (local) {
    metaCache.set(id, local);
    return local;
  }
  if (!allowRemote) return undefined;
  try {
    const r = await api.get<{ asset: RemoteAssetMeta }>(`/assets/${id}`);
    const variants: LocalAssetMeta['variants'] = {};
    for (const [k, v] of Object.entries(r.asset.variants)) {
      if (v) variants[k as VariantName] = { w: v.w, h: v.h };
    }
    const meta: LocalAssetMeta = {
      id: r.asset.id,
      mime: r.asset.mime,
      name: r.asset.name,
      bytes: r.asset.bytes,
      width: r.asset.width,
      height: r.asset.height,
      variants,
      uploaded: true,
    };
    metaCache.set(id, meta);
    await set(metaKey(id), meta, assetsIdb);
    return meta;
  } catch {
    return undefined;
  }
}

/** Resolves the requested variant, or the next larger one that exists. */
export async function resolveVariant(id: string, wanted: VariantName): Promise<VariantName | undefined> {
  const meta = await getAssetMeta(id);
  if (!meta) return undefined;
  const start = VARIANT_ORDER.indexOf(wanted);
  for (let i = Math.max(0, start); i < VARIANT_ORDER.length; i++) {
    const v = VARIANT_ORDER[i]!;
    if (meta.variants[v]) return v;
  }
  return undefined;
}

export async function getAssetBlob(
  id: string,
  wanted: VariantName,
  allowRemote = true,
): Promise<Blob | undefined> {
  const v = await resolveVariant(id, wanted);
  if (!v) return undefined;
  const local = await get<Blob>(blobKey(id, v), assetsIdb);
  if (local) return local;
  if (!allowRemote) return undefined;
  const meta = await getAssetMeta(id);
  if (!meta?.uploaded) return undefined;
  try {
    const res = await fetch(assetsUrl(id, v), { credentials: 'same-origin' });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    await set(blobKey(id, v), blob, assetsIdb);
    return blob;
  } catch {
    return undefined;
  }
}

export async function deleteAssetLocally(id: string): Promise<void> {
  metaCache.delete(id);
  await Promise.all([del(metaKey(id), assetsIdb), ...VARIANT_ORDER.map((v) => del(blobKey(id, v), assetsIdb))]);
  for (const v of VARIANT_ORDER) urlCache.delete(`${id}:${v}`);
}

// Object URLs are cached for the page lifetime.
const urlCache = new Map<string, Promise<string | undefined>>();

export function getAssetUrl(id: string, wanted: VariantName): Promise<string | undefined> {
  const key = `${id}:${wanted}`;
  let p = urlCache.get(key);
  if (!p) {
    p = (async () => {
      const resolved = await resolveVariant(id, wanted);
      if (!resolved) return undefined;
      const resolvedKey = `${id}:${resolved}`;
      if (resolved !== wanted) {
        const existing = urlCache.get(resolvedKey);
        if (existing) return existing;
      }
      const blob = await getAssetBlob(id, resolved);
      if (!blob) return undefined;
      const url = URL.createObjectURL(blob);
      if (resolved !== wanted) urlCache.set(resolvedKey, Promise.resolve(url));
      return url;
    })();
    urlCache.set(key, p);
    // don't cache failures forever: a missing asset may arrive from the server later
    p.then((u) => {
      if (!u) urlCache.delete(key);
    });
  }
  return p;
}

/** Object URL for an asset variant once loaded; undefined while loading or missing. Retries while missing. */
export function useAssetUrl(id: string, wanted: VariantName): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay = 2000;
    const load = () => {
      getAssetUrl(id, wanted).then((u) => {
        if (cancelled) return;
        if (u) setUrl(u);
        else {
          timer = setTimeout(load, delay);
          delay = Math.min(delay * 2, 30000);
        }
      });
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id, wanted]);
  return url;
}
